import {
  IContractAgreementMutation,
  IContractDistributionMutation,
  IContractDistributionShareMutation,
  IContractSectionMutation,
} from 'services/contract/types';
import { AGREEMENT_TYPE } from 'services/templates/agreement-template/types';
import {
  IContractTemplateTreeItemForm,
  IDistributionType,
  SETTLEMENT_TYPE,
  TEMPLATE_TYPE,
} from 'services/templates/types';
import {
  isPerUnitSettlement,
  mapShareFormToContractMutation,
} from 'utils/components/contract-template/distribution/settlementType.utils';
import { mapContractSectionsFromItems } from 'utils/components/contract-template/section/contractSection.utils';

interface ISeparateContractTreeItems {
  customSections: IContractSectionMutation[];
  agreements: IContractAgreementMutation[];
  digitalDistribution: IContractDistributionMutation | null;
  physicalDistribution: IContractDistributionMutation | null;
  merchDistribution: IContractDistributionMutation | null;
  channelDistribution: IContractDistributionMutation | null;
}

export const getDistributionWithShares = (
  items: IContractTemplateTreeItemForm[],
  templateType: IDistributionType
): IContractDistributionMutation | null => {
  const itemsWithPosition = items.map((items, index) => ({
    ...items,
    position: index,
  }));

  const distributionItem = itemsWithPosition.find(
    item => item.type === templateType
  );

  if (!distributionItem || distributionItem.type !== templateType) {
    return null;
  }

  const settlementType =
    distributionItem.settlementType ?? SETTLEMENT_TYPE.Percentage;

  let defaultPercentage = 0;
  let defaultAmountPerUnit = 0;

  const distributionShares =
    distributionItem.children.reduce<IContractDistributionShareMutation[]>(
      (acc, item: IContractTemplateTreeItemForm) => {
        if (
          item.type !== TEMPLATE_TYPE.agreement ||
          !item.content.distributionTemplateShares
        ) {
          return acc;
        }

        if (item.content.type === AGREEMENT_TYPE.defaultShare) {
          if (isPerUnitSettlement(settlementType)) {
            defaultAmountPerUnit =
              item.content.distributionTemplateShares.amountPerUnit ?? 0;
          } else {
            defaultPercentage =
              item.content.distributionTemplateShares.percent ?? 0;
          }
          return acc;
        }

        if (item.content.type === AGREEMENT_TYPE.shares) {
          const { id: salesChannelId, percent, amountPerUnit } =
            item.content.distributionTemplateShares;

          acc.push(
            mapShareFormToContractMutation(settlementType, salesChannelId, {
              percent,
              amountPerUnit,
            })
          );
        }

        return acc;
      },
      []
    );

  return {
    title: distributionItem.content,
    position: distributionItem.position,
    settlementType,
    ...(isPerUnitSettlement(settlementType)
      ? { defaultAmountPerUnit }
      : { defaultPercentage }),
    shares: distributionShares,
  };
};

export const separateContractTreeItems = (
  items: IContractTemplateTreeItemForm[]
): ISeparateContractTreeItems => {
  const itemsWithPosition = items.map((items, index) => ({
    ...items,
    position: index,
  }));

  const agreements: IContractAgreementMutation[] = [];
  for (const item of itemsWithPosition) {
    if (item.type === TEMPLATE_TYPE.agreement) {
      const isParagraph = item.content.type === AGREEMENT_TYPE.paragraph;
      const agreementContent = isParagraph
        ? item.content.paragraphAgreementTemplate
        : item.content.textAgreementTemplate;

      agreements.push({
        textAgreementRequest: {
          position: item.position,
          title: agreementContent?.title ?? '',
          defaultValue: agreementContent?.defaultValue ?? '',
        },
      });
    }
  }

  const customSections = mapContractSectionsFromItems(itemsWithPosition);

  return {
    customSections,
    agreements,
    digitalDistribution: getDistributionWithShares(
      items,
      TEMPLATE_TYPE.digitalDistribution
    ),
    physicalDistribution: getDistributionWithShares(
      items,
      TEMPLATE_TYPE.physicalDistribution
    ),
    merchDistribution: getDistributionWithShares(
      items,
      TEMPLATE_TYPE.merchDistribution
    ),
    channelDistribution: getDistributionWithShares(
      items,
      TEMPLATE_TYPE.channelDistribution
    ),
  };
};
